import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { 
  Mail, 
  Github, 
  Linkedin, 
  Award,
  Rocket,
  Target,
  Zap,
  Star,
  TrendingUp,
  Users,
  Globe,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BookOpen,
  Code,
  Cloud,
  Palette,
  Lightbulb
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const Founder = () => {
  const expertise = [
    { icon: <Code className="w-5 h-5" />, title: "Full-Stack Web Development" },
    { icon: <Palette className="w-5 h-5" />, title: "UI/UX & Product Design" },
    { icon: <Cloud className="w-5 h-5" />, title: "Cloud & Firebase Solutions" },
    { icon: <BookOpen className="w-5 h-5" />, title: "Education Technology" },
    { icon: <Rocket className="w-5 h-5" />, title: "Startup Solutions" },
    { icon: <Zap className="w-5 h-5" />, title: "Process Automation" }
  ];

  const achievements = [
    { value: "500+", label: "Projects Delivered", icon: <Target className="w-5 h-5" /> },
    { value: "250+", label: "Happy Clients", icon: <Users className="w-5 h-5" /> },
    { value: "15+", label: "Countries Served", icon: <Globe className="w-5 h-5" /> },
    { value: "99%", label: "Client Satisfaction", icon: <Star className="w-5 h-5" /> }
  ];

  const socialLinks = [
    { icon: <Linkedin className="w-5 h-5" />, label: "LinkedIn", href: "https://linkedin.com", color: "from-blue-600 to-blue-700" },
    { icon: <Github className="w-5 h-5" />, label: "GitHub", href: "https://github.com/", color: "from-gray-800 to-gray-900" },
    { icon: <Mail className="w-5 h-5" />, label: "Email", href: "mailto:contact@skyretieducore.com", color: "from-red-500 to-red-600" }
  ];

  const values = [
    { icon: "💡", title: "Innovation First", desc: "Continuously exploring cutting-edge technologies" },
    { icon: "🎯", title: "Client-Centric", desc: "Solutions tailored to specific business needs" },
    { icon: "⚡", title: "Speed & Efficiency", desc: "Rapid development without compromising quality" },
    { icon: "🌱", title: "Sustainable Growth", desc: "Building solutions that scale with your business" }
  ];

  return (
    <>
      <Helmet>
        <title>Founder | Skyreti EduCore</title>
        <meta
          name="description"
          content="Meet the founder of Skyreti EduCore and learn about our mission, journey, and achievements."
        />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-cyan-50/50" />
          <div
  className="absolute inset-0 opacity-40 pointer-events-none"
  style={{
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  }}
/>

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <Badge className="mb-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
                <Sparkles className="w-4 h-4 mr-2" />
                Visionary Leadership
              </Badge>

              {/* Founder Profile */}
              <div className="relative mb-10">
                <div className="relative mx-auto w-40 h-40 md:w-48 md:h-48">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-600 rounded-full animate-spin-slow" />
                  <div className="absolute inset-4 bg-white rounded-full flex items-center justify-center">
                    <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                      SR
                    </div>
                  </div>
                </div>
                
                {/* Animated Rings */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="absolute w-48 h-48 md:w-56 md:h-56 border-2 border-blue-200 rounded-full animate-ping opacity-20" />
                  <div className="absolute w-56 h-56 md:w-64 md:h-64 border-2 border-cyan-200 rounded-full animate-pulse opacity-10" />
                </div>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">
                Meet the <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Founder</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                The visionary behind Skyreti EduCore's mission to transform digital education and startup solutions
              </p>
            </motion.div>
          </div>
        </section>

        {/* About Founder */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <Card className="bg-white border-none shadow-xl rounded-3xl overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-cyan-400/10 rounded-full transform translate-x-16 -translate-y-16" />
                
                <CardContent className="p-8 md:p-12">
                  <div className="flex items-center gap-3 mb-6">
                    <Lightbulb className="w-6 h-6 text-blue-600" />
                    <h2 className="text-2xl font-bold text-gray-900">Vision & Mission</h2>
                  </div>
                  
                  <div className="space-y-6">
                    <p className="text-gray-700 text-lg leading-relaxed">
                      Skyreti EduCore was founded by a passionate technologist with a clear vision — to modernize education and startups through scalable, affordable, and impactful digital solutions.
                    </p>
                    
                    <p className="text-gray-700 leading-relaxed">
                      With hands-on experience across full-stack development, UI/UX, automation, and cloud platforms, the focus is on building long-term digital ecosystems rather than short-term projects.
                    </p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6">
                      {values.map((value, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="text-center p-4 bg-gray-50 rounded-xl"
                        >
                          <div className="text-2xl mb-2">{value.icon}</div>
                          <div className="font-semibold text-gray-900 text-sm mb-1">{value.title}</div>
                          <div className="text-xs text-gray-600">{value.desc}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Expertise & Achievements */}
        <section className="py-16 px-4 bg-gradient-to-r from-blue-50/30 to-cyan-50/30">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Expertise */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="bg-white border-none shadow-lg rounded-2xl overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <Zap className="w-6 h-6 text-blue-600" />
                      <h2 className="text-2xl font-bold text-gray-900">Core Expertise</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {expertise.map((skill, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="flex items-center gap-3 p-3 bg-blue-50/50 rounded-lg group hover:bg-blue-100 transition-colors"
                        >
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                            <div className="text-blue-600">{skill.icon}</div>
                          </div>
                          <span className="text-gray-700 font-medium text-sm">{skill.title}</span>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Achievements */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <Card className="bg-white border-none shadow-lg rounded-2xl overflow-hidden">
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <Award className="w-6 h-6 text-amber-600" />
                      <h2 className="text-2xl font-bold text-gray-900">Key Achievements</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {achievements.map((achievement, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="text-center p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
                        >
                          <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 mb-3 mx-auto">
                            <div className="text-blue-600">{achievement.icon}</div>
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mb-1">{achievement.value}</div>
                          <div className="text-sm text-gray-600">{achievement.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Connect Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gradient-to-r from-gray-900 to-black text-white border-none rounded-3xl overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-500/20 to-cyan-400/20 rounded-full transform translate-x-32 -translate-y-32" />
                
                <CardContent className="p-8 md:p-12 relative">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-4">Connect With the Founder</h2>
                    <p className="text-gray-300 max-w-lg mx-auto">
                      Reach out for collaborations, insights, or to discuss your next digital project
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    {socialLinks.map((link, index) => (
                      <motion.a
                        key={index}
                        href={link.href}
                        target="_blank"
                        rel="noreferrer"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -5 }}
                        className={`bg-gradient-to-r ${link.color} rounded-2xl p-6 text-center hover:shadow-xl transition-all duration-300 group`}
                      >
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/20 mb-4 group-hover:scale-110 transition-transform">
                          <div className="text-white">{link.icon}</div>
                        </div>
                        <div className="font-semibold text-lg mb-1">{link.label}</div>
                        <div className="text-sm text-white/70">Click to connect</div>
                      </motion.a>
                    ))}
                  </div>

                  <div className="text-center pt-6 border-t border-white/10">
                    <p className="text-gray-300 mb-4">
                      Typically responds within 24 hours
                    </p>
                    <div className="inline-flex items-center gap-2 text-sm text-gray-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Available for consultations and partnerships
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-cyan-500/10 rounded-3xl" />
              
              <div className="relative p-8 md:p-12">
                <Rocket className="w-16 h-16 text-blue-600 mx-auto mb-6" />
                
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Let's Build Something Impactful Together
                </h2>
                
                <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
                  Have an idea or project that could transform your organization? Let's discuss how we can bring your vision to life.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                 <motion.a
  href="/#contact"
  whileHover={{ scale: 1.05 }}
  whileTap={{ scale: 0.95 }}
  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl transition-all"
>
  Get in Touch
  <ArrowRight className="w-5 h-5" />
</motion.a>

                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white border-2 border-black text-black font-semibold rounded-xl hover:bg-black hover:text-white transition-all"
                  >
                    View Portfolio
                    <ArrowRight className="w-5 h-5" />
                  </motion.button>
                </div>

                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                  {[
                    { label: "Strategy Session", value: "Free 30-min" },
                    { label: "Response Time", value: "< 24 hrs" },
                    { label: "Project Success", value: "99%" },
                    { label: "Global Support", value: "24/7" }
                  ].map((item, index) => (
                    <div key={index} className="text-center">
                      <div className="text-lg font-bold text-gray-900 mb-1">{item.value}</div>
                      <div className="text-sm text-gray-600">{item.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      `}</style>
    </>
  );
};

export default Founder;